import {
  ClientStatusType,
  CreatePlanBody,
  ExpirationType,
  IntervalType,
  LimitCheckinsType,
  Plan,
  PlanType,
  UpdatePlanBody,
} from "../../../apis/subscriptionApi";
import { PlanTemplate } from "../PlanTemplates/plan-templates";
import { PlanForm } from "./PlanDrawer.types";

export function getPlanDrawerTitle(planType?: PlanType) {
  switch (planType) {
    case "recurring":
      return "Recurring plan";
    case "non-recurring":
      return "Non-recurring plan";
    case "session-pack":
      return "Punchcard";
    default:
      return "";
  }
}

export const clientStatusOptions: { label: string; value: ClientStatusType }[] =
  [
    {
      label: "Member",
      value: "member",
    },
    {
      label: "Non member",
      value: "non-member",
    },
    {
      label: "Lead",
      value: "lead",
    },
  ];

export const intervalTypeOptions: { label: string; value: IntervalType }[] = [
  {
    label: "Day(s)",
    value: "day",
  },
  {
    label: "Week(s)",
    value: "week",
  },
  {
    label: "Month(s)",
    value: "month",
  },
  {
    label: "Year(s)",
    value: "year",
  },
];

export const attendanceLimitOptions: {
  label: string;
  value: "unlimited" | "limited";
}[] = [
    {
      label: "Unlimited",
      value: "unlimited",
    },
    {
      label: "Limited",
      value: "limited",
    },
  ];

export const classAccessOptions: {
  label: string;
  value: "all" | "some" | "none";
}[] = [
    {
      label: "All classes",
      value: "all",
    },
    {
      label: "Some classes",
      value: "some",
    },
    {
      label: "No classes access",
      value: "none",
    },
  ];

export const sessionRecurringOptions: {
  label: string;
  value: LimitCheckinsType;
}[] = [
    {
      label: "per billing period",
      value: "billing-period",
    },
    {
      label: "per week",
      value: "week",
    },
    {
      label: "per month",
      value: "month",
    },
  ];

export const sessionNonRecurringOptions: {
  label: string;
  value: LimitCheckinsType;
}[] = [
    {
      label: "per week",
      value: "week",
    },
    {
      label: "per month",
      value: "month",
    },
  ];

export function createPlanFormDefaultValues(
  planType: PlanType = "recurring",
  planTemplate?: PlanTemplate,
): Partial<PlanForm> {
  const useTemplateDefaults = !!planTemplate;
  if (useTemplateDefaults) {
    return planTemplate.planFormValues;
  }

  return {
    clientStatus: clientStatusOptions[0],
    price: 100,
    billEveryCount: 1,
    billEveryInterval: intervalTypeOptions[2],
    publishToPublic: true,
    openGym: true,
    recursForever: true,
    hybridAf: false,
    attendanceLimit: attendanceLimitOptions[0],
    classAccess: classAccessOptions[0],
    sellOnMemberApp: true,
    sellOnStaffApp: true,
    sellOnWebsitePlanList: true,
    allowClassBooking: true,
    sessionsPeriod:
      planType === "recurring"
        ? sessionRecurringOptions[0]
        : (planType === "non-recurring" ? sessionNonRecurringOptions[0] : undefined),
    sessionsExpireInterval: intervalTypeOptions[0],
  };
}

export function convertToCreatePlanBody(
  planType: PlanType,
  values: PlanForm,
): CreatePlanBody {
  // billing
  const setupFee = (values.hasSetupFee ? values.setupFee : 0) ?? 0;
  const occurrences = values.recursForever ? undefined : values.occurrences;

  // classes
  const allowCheckins = values.classAccess.value !== "none";
  let limitCheckins = values.attendanceLimit.value === "limited";
  let limitCheckinsQuantity = limitCheckins ? values.sessionsCount : undefined;
  let limitCheckinsType = limitCheckins
    ? values.sessionsPeriod?.value
    : undefined;
  const checkinRestrictedCalendarTypeUuids =
    values.classAccess.value === "some"
      ? values.classes?.map((c) => c.uuid)
      : [];
  if (planType === "session-pack") {
    limitCheckins = true;
    limitCheckinsQuantity = values.sessionsCount;
    limitCheckinsType = undefined;
  }
  if (values.classAccess.value === "none" && !values.openGym) {
    limitCheckins = false;
    limitCheckinsQuantity = undefined;
    limitCheckinsType = undefined;
  }

  // appointments
  const appointmentCredits =
    values.appointmentCredits?.map(({ appointmentType, ...rest }) => ({
      ...rest,
      appointmentTypeUuid: appointmentType.uuid,
      value: rest.value === null ? undefined : rest.value,
    })) || [];

  // other
  const quantityAvailable = values.hasLimit ? values.quantityAvailable : 0;

  // sales
  const showOnMemberPortal = values.publishToPublic
    ? values.sellOnMemberApp
    : false;
  const showKioskMode = values.publishToPublic ? values.sellOnStaffApp : false;
  const showOnLandingPages = values.publishToPublic
    ? values.sellOnWebsitePlanList
    : false;
  const allowClassBooking =
    values.publishToPublic && values.classAccess.value !== "none"
      ? values.allowClassBooking
      : false;

  // expiration and interval
  let expiration: number | undefined = undefined;
  let expirationType: ExpirationType | undefined = undefined;
  let interval: number | undefined = undefined;
  let intervalType: IntervalType | undefined = undefined;
  if (planType === "recurring") {
    interval = values.billEveryCount;
    intervalType = values.billEveryInterval.value;
  } else if (planType === "non-recurring") {
    expiration = values.billEveryCount;
    expirationType = values.billEveryInterval?.value;
  } else if (planType === "session-pack") {
    expiration = values.sessionsExpireCount;
    expirationType = values.sessionsExpireInterval?.value;
  }

  return {
    // basic
    type: planType,
    name: values.name,
    clientStatus: values.clientStatus.value,
    planCategoryUuid: values.planCategory.uuid,

    // billing
    amount: values.price,
    interval,
    intervalType,
    recursForever: values.recursForever,
    occurrences,
    setupFee,
    taxRateUuid: values.taxRate?.uuid,
    expiration,
    expirationType,

    // classes
    allowCheckins,
    checkinRestrictedCalendarTypeUuids,
    allowOpenGymCheckins: values.openGym,
    limitCheckins,
    limitCheckinsQuantity,
    limitCheckinsType,

    // appointments
    appointmentCredits,

    // other
    planIs24HourAccess: values.hybridAf,
    limitQuantity: values.hasLimit,
    quantityAvailable,
    priorityBooking: values.priorityBooking,
    priorityBookingMinutes: values.priorityBooking ? values.priorityHoursInAdvance * 60 : 0, // convert hours to minutes

    perPersonLimit: values.perPersonLimit,
    perPersonLimitNum: values.perPersonLimit ? values.perPersonLimitNum : 1,

    hasFixedBillingDayOfMonth: values.hasFixedBillingDayOfMonth,
    fixedBillingDayOfMonth: values.fixedBillingDayOfMonth,
    allowCustomizableStartDate: !values.forcePurchaseStartDate,

    // sales
    publishToPublic: values.publishToPublic,
    showOnMemberPortal,
    showKioskMode,
    showOnLandingPages,
    confirmationMessage: values.confirmationMessage,
    allowClassBooking,
    description: values.shortDescription,
    additionalInfo: values.longDescription,
  };
}

export function convertToUpdatePlanBody(
  planUuid: string,
  planType: PlanType,
  values: PlanForm,
  update?: { price: boolean; sessions: boolean },
): UpdatePlanBody {
  let updateSubscriptions: UpdatePlanBody["updateSubscriptions"];
  if (update?.price || update?.sessions) {
    updateSubscriptions = {
      amount: update?.price,
      checkins: update?.sessions,
    };
  }
  return {
    ...convertToCreatePlanBody(planType, values),
    planUuid,
    updateSubscriptions,
  };
}

export function convertToPlanForm(plan: Plan, duplicate = false): PlanForm {
  let classAccessValue = !plan.allowCheckins ? "none" : "all";
  if (plan.checkinRestrictedCalendarTypes?.length) {
    classAccessValue = "some";
  }

  const attendanceLimitValue = plan.limitCheckins ? "limited" : "unlimited";

  // expiration and interval
  let billEveryCount: number | undefined = undefined;
  let billEveryInterval:
    | {
      value: IntervalType;
      label: string;
    }
    | undefined = undefined;
  let sessionsExpireCount: number | undefined = undefined;
  let sessionsExpireInterval:
    | {
      value: IntervalType;
    }
    | undefined = undefined;
  if (plan.type === "recurring") {
    billEveryCount = plan.interval;
    billEveryInterval = intervalTypeOptions.find(
      (o) => o.value === plan.intervalType,
    );
  } else if (plan.type === "non-recurring") {
    billEveryCount = plan.expiration;
    billEveryInterval = intervalTypeOptions.find(
      (o) => o.value === plan.expirationType,
    );
  } else if (plan.type === "session-pack") {
    sessionsExpireCount = plan.expiration;
    sessionsExpireInterval = intervalTypeOptions.find(
      (o) => o.value === plan.expirationType,
    );
  }

  const name = duplicate ? `Duplicate of ${plan.name}` : plan.name;

  return {
    name,
    clientStatus: clientStatusOptions.find(
      (o) => o.value === plan.clientStatus,
    ),
    planCategory: plan.planCategory,
    price: plan.amount,
    billEveryCount,
    billEveryInterval,
    recursForever: plan.recursForever,
    occurrences: plan.occurrences,
    hasSetupFee: plan.setupFee > 0,
    setupFee: plan.setupFee === 0 ? undefined : plan.setupFee,
    taxRate: plan.taxRate,
    classAccess: classAccessOptions.find((o) => o.value === classAccessValue),
    openGym: plan.allowOpenGymCheckins,
    attendanceLimit: attendanceLimitOptions.find(
      (o) => o.value === attendanceLimitValue,
    ),
    sessionsCount: plan.limitCheckinsQuantity,
    sessionsPeriod: sessionRecurringOptions.find(
      (o) => o.value === plan.limitCheckinsType,
    ),
    sessionsExpire: plan.expiration > 0,
    sessionsExpireCount,
    sessionsExpireInterval,
    classes: plan.checkinRestrictedCalendarTypes,
    appointmentCredits: plan.appointmentCredits,
    hybridAf: plan.planIs24HourAccess,
    hasLimit: plan.limitQuantity,
    quantityAvailable: plan.quantityAvailable,
    publishToPublic: plan.publishToPublic,
    sellOnMemberApp: plan.showOnMemberPortal,
    sellOnStaffApp: plan.showKioskMode,
    sellOnWebsitePlanList: plan.showOnLandingPages,
    confirmationMessage: plan.confirmationMessage,
    allowClassBooking: plan.allowClassBooking,
    shortDescription: plan.description,
    longDescription: plan.additionalInfo,
    priorityBooking: plan.priorityBookingMinutes > 0,
    priorityHoursInAdvance: plan.priorityBookingMinutes / 60, // convert minutes to hours
    perPersonLimit: plan.perPersonLimit,
    perPersonLimitNum: plan.perPersonLimitNum,
    hasFixedBillingDayOfMonth: plan.hasFixedBillingDayOfMonth,
    fixedBillingDayOfMonth: plan.fixedBillingDayOfMonth,
    forcePurchaseStartDate: !plan.allowCustomizableStartDate,
  };
}
